import React, { useEffect, useState } from 'react';
import { apiFetch } from './api';
import { useCompanyAbout } from './AboutPanel';

const copyText = async (text) => {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch (_) {
      // Clipboard access can be rejected on an HTTP address (especially on
      // phones), so fall through to the selection-based browser fallback.
    }
  }
  const area = document.createElement('textarea');
  area.value = text;
  area.style.position = 'fixed';
  area.style.opacity = '0';
  document.body.appendChild(area);
  area.select();
  document.execCommand('copy');
  area.remove();
};

const shortDate = (value) => {
  const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return match ? `${match[3]}/${match[2]}/${match[1].slice(2)}` : value;
};

const AnalysisPanel = ({ link, name, canEdit = false, bseCode, nseCode }) => {
  const about = useCompanyAbout(link);
  const aboutText = about?.about ? about.about.replace(/\s*\n\s*/g, ' ') : '';
  const [versions, setVersions] = useState([]);
  const [index, setIndex] = useState(0);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const [transcripts, setTranscripts] = useState([]);
  const [transcriptBusy, setTranscriptBusy] = useState(false);
  const [transcriptMessage, setTranscriptMessage] = useState('');
  const [showTranscripts, setShowTranscripts] = useState(false);

  const load = async () => {
    if (!link) return;
    const response = await apiFetch(`/api/analysis/${encodeURIComponent(link)}`);
    const body = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(body.error || 'Unable to load analysis');
    setVersions(body.versions || []);
    setIndex(0);
  };

  useEffect(() => {
    setVersions([]);
    setIndex(0);
    setDraft('');
    setError('');
    load().catch((requestError) => setError(requestError.message));
  }, [link]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadTranscripts = async () => {
    if (!link) return;
    const response = await apiFetch(`/api/transcripts/${encodeURIComponent(link)}`);
    const body = await response.json().catch(() => ({}));
    if (response.ok) setTranscripts(body.transcripts || []);
  };

  useEffect(() => {
    setTranscripts([]);
    setTranscriptMessage('');
    setShowTranscripts(false);
    loadTranscripts().catch(() => {});
  }, [link]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchLatestTranscript = async () => {
    setTranscriptBusy(true);
    setTranscriptMessage('');
    try {
      const response = await apiFetch(`/api/transcripts/${encodeURIComponent(link)}/download`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bse: bseCode, nse: nseCode }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || 'Could not get transcripts');
      setTranscripts(body.transcripts || []);
      setTranscriptMessage(body.message || 'Ready');
    } catch (requestError) {
      setTranscriptMessage(requestError.message);
    } finally {
      setTranscriptBusy(false);
    }
    setShowTranscripts(true);
  };

  const copyTranscript = async (transcript) => {
    setTranscriptMessage('');
    try {
      const response = await apiFetch(`/t/${encodeURIComponent(link)}?n=${transcript.n}`);
      if (!response.ok) throw new Error('Transcript unavailable');
      await copyText(await response.text());
      setTranscriptMessage(`T${transcript.n} copied`);
    } catch (requestError) {
      setTranscriptMessage(requestError.message);
    }
  };

  const save = async () => {
    if (!canEdit || !draft.trim()) return;
    setBusy(true);
    setError('');
    try {
      const response = await apiFetch(`/api/analysis/${encodeURIComponent(link)}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: draft.trim() }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || 'Unable to save analysis');
      setVersions(body.versions || []);
      setIndex(0);
      setDraft('');
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  const selected = versions[index];
  return (
    <div className="analysis-panel">
      <div className="analysis-panel-head">
        <h4>Analysis</h4>
        {/* The header had a gap between the title and its buttons; the company
            being looked at is the useful thing to put there. Falls back to the
            link (the screener.in slug) when no display name is available. */}
        {(name || link) && (
          <span className={`analysis-company${aboutText ? ' with-about' : ''}`} title={name || link}>
            {name || link}
            {aboutText && <span className="analysis-about">: {aboutText}</span>}
          </span>
        )}
        <div className="analysis-head-actions">
          {!showTranscripts && versions.length > 0 && (
            <div className="analysis-version-nav">
              <button type="button" disabled={index >= versions.length - 1} onClick={() => setIndex((value) => value + 1)}>← Older</button>
              <span>{index + 1}/{versions.length}</span>
              <button type="button" disabled={index === 0} onClick={() => setIndex((value) => value - 1)}>Newer →</button>
            </div>
          )}
          {showTranscripts
            ? <button type="button" className="transcript-toggle" onClick={() => setShowTranscripts(false)}>Analysis</button>
            : (
              <button type="button" className="transcript-toggle" disabled={transcriptBusy} onClick={fetchLatestTranscript}>
                {transcriptBusy ? 'Getting…' : 'Get T'}
              </button>
            )}
        </div>
      </div>
      {showTranscripts
        ? (
          <div className="transcript-controls">
            {transcripts.map((transcript) => (
              <button key={transcript.filename} type="button" onClick={() => copyTranscript(transcript)} title={`Copy transcript ${transcript.n}`}>
                T{transcript.n} {shortDate(transcript.date)} Copy
              </button>
            ))}
            {transcripts.length === 0 && <small>No transcripts yet</small>}
            {transcriptMessage && <small>{transcriptMessage}</small>}
          </div>
        )
        : (
          <>
            {selected && <><small className="analysis-date">{new Date(selected.created_at).toLocaleString('en-GB')}</small><pre>{selected.analysis_text}</pre></>}
            {canEdit && (
              <div className="analysis-editor">
                <textarea rows="2" placeholder={selected ? 'Paste replacement analysis…' : 'Paste analysis…'} value={draft} onChange={(event) => setDraft(event.target.value)} />
                <button type="button" disabled={busy || !draft.trim()} onClick={save}>{busy ? '…' : 'Save'}</button>
              </div>
            )}
            {error && <small className="analysis-error">{error}</small>}
          </>
        )}
    </div>
  );
};

export default AnalysisPanel;
